import { useSelector } from 'react-redux';

import { FILE_PATH_IN_REPORTS } from '../../../../../../../../shared/featureFlags/flagNames';
import { DependencyInfoOptions, dependencyInfoOptionsWithDescriptions } from '../../../../../../../../shared/reports';
import { window } from '../../../../../../../globals';
import { isFeatureEnabled } from '../../../../../../../helpers/features';
import useIsLicenseConcludedEnabled from '../../../../../../../hooks/features/useIsLicenseConcludedEnabled';
import * as reportGeneration from '../../../../../../../redux/reportGeneration';
import FilterCardCheckboxes from '../../../../../../generic/Filter/FilterCardCheckboxes';
import { ReportCategories } from '../../../utils';
import type { SelectOption, SetState } from '../../../../../../../types';
import type { DependencyInfoOption, ReportCategory } from '../../../../shared/types';
import type { FormatSelection } from '../FormatSelect';

type Props = {
  format: FormatSelection;
  values: SelectOption[];
  category: ReportCategory;
  setValues: SetState<SelectOption[]>;
};

const DependencyMetadataCheckboxGroup = ({ format, values, category, setValues }: Props) => {
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);

  const isFilePathInReports = isFeatureEnabled(FILE_PATH_IN_REPORTS);
  const projectType = useSelector(reportGeneration.selectors.getProjectType);
  const options = Object.entries(DependencyInfoOptions)
    .filter(([key]) => {
      if (key === 'FilePath') {
        return isFilePathInReports;
      }
      if (key === 'Project') {
        return projectType === 'releaseGroup' && category === ReportCategories.Licensing;
      }

      if (key === 'ConcludedLicense' && !isLicenseConcludedEnabled) {
        return false;
      }

      const licensingOnlyDependencyMetadata = new Set(['IssueNotes', 'Source']);
      if (licensingOnlyDependencyMetadata.has(key)) {
        return category === ReportCategories.Licensing;
      }

      const cycloneDXFormats = new Set(['CycloneDX (JSON)', 'CycloneDX (XML)']);
      if (key === 'ProjectUrl' && cycloneDXFormats.has(format.value)) {
        return false;
      }

      return true;
    })
    .map(([key, value]) => ({
      label: value,
      value: key,
      tooltip: dependencyInfoOptionsWithDescriptions[key as DependencyInfoOption],
    }));

  return (
    <div className="width-full" data-test-id="sbom-filters-dependency-metadata">
      <FilterCardCheckboxes label="Dependency Metadata" options={options} values={values} onChange={setValues} tooltipPlacement="left" />
    </div>
  );
};

export default DependencyMetadataCheckboxGroup;
